import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { trigger, style, transition, animate, keyframes } from '@angular/animations';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StatusRow } from 'src/app/modules/shared/components/status-container/status-container.component';
import { AdminUserService } from 'src/app/services/admin-user.service';
import { DblistService, ListTimeseriesDataType } from 'src/app/services/dblist.service';
import { Outcome, OutcomeFacilityData, OutcomeFacilityInstrumentData, OutcomesService, OutcomeWorkflowAction, WorkflowAction } from 'src/app/services/outcomes.service';
import { IwipService, Station } from 'src/app/services/iwip.service';
import { StatusBadge } from 'src/app/modules/shared/components/status-icon/status-icon.component';
@Component({
  selector: 'app-outcomes-details',
  templateUrl: './outcomes-details.component.html',
  styleUrls: ['./outcomes-details.component.scss'],
  animations: [
    trigger('openClose', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('0.25s', keyframes([
          style({ height: 0, opacity: 0, offset: 0 }),
          style({ height: '*', opacity: 1, offset: 1 }),
        ]))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, }),
        animate('0.25s', keyframes([
          style({ height: '*', opacity: 1, offset: 0 }),
          style({ height: 0, opacity: 0, offset: 1 }),
        ]))
      ])
    ])
  ]
})
export class OutcomesDetailsComponent implements OnInit {

  url: string;
  outcomeId: number;
  outcome: Outcome;
  outcomeData: OutcomeFacilityData[];
  workflowActions: WorkflowAction[];
  statusRows: StatusRow[];

  outcomeProvisionalSubmitted: string;
  outcomeProvisionalAccepted: string;
  outcomeProvisionalStatus: string;
  reportSubmitted: string;
  reportAccepted: string;
  outcomeFinalDataStatus: string;
  outcomeFinalSubmitted: string;
  outcomeFinalAccepted: string;

  timeseriesDataTypes: ListTimeseriesDataType[] = [];

  stationList: Station[] = [];

  isDNR: boolean = false;

  stepOneLabel: string = "Data Submission";
  stepTwoLabel: string = "Data Review";
  stepThreeLabel: string = "Finalize Outcomes";

  activeIds: string[] = [];
  accFacilityDataArr: string[] = [];

  unsubscribe: Subject<any> = new Subject;

  @ViewChild('rejectReasonModal', { static: false }) private modalContent: TemplateRef<any>;
  private modalRef: NgbModalRef;
  rejectComment: string;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private adminUserService: AdminUserService,
    private outcomesService: OutcomesService,
    private dbListService: DblistService,
    private iwipService: IwipService,
    private modalService: NgbModal
  ) { }

  ngOnInit() {
    var routeSnapshot = this.route.snapshot;
    this.url = routeSnapshot.url[0].path;
    this.outcomeId = +routeSnapshot.paramMap.get('id');

    this.outcomesService.getOutcome(this.outcomeId, true);
    this.outcomesService.currentOutcome
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(outcome => {
        if (outcome) {
          this.outcome = outcome;
          this.workflowActions = outcome.allowedWorkflowActions;
          this.generateStatusRows(outcome);
        }
      });

    this.outcomesService.currentOutcomeData
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(outcomeData => {
        if (outcomeData) {
          this.outcomeData = outcomeData;
        }
      });

    this.dbListService.getListTimeseriesDataType();
    this.dbListService.listTimeseriesDataType$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(listTimeseriesDataTypes => {
        this.timeseriesDataTypes = listTimeseriesDataTypes;
      });

    this.iwipService.getStationList();
    this.iwipService.stationList
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(stationList => {
        this.stationList = stationList.sort((a, b) => a.StationName.localeCompare(b.StationName));
      });

    this.adminUserService.checkIsDNR();
    this.adminUserService.isDNR
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dnrClaim => {
        if (!!dnrClaim) {
          this.isDNR = dnrClaim.claimValue === "yes";
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  changeUrl = (url: string) => {
    this.url = url;
    this.location.replaceState(`${url}/${this.outcomeId}`);
  }

  showSection(name: string) {
    if (!!this.activeIds) {
      if (this.activeIds[0] === name && this.activeIds.length === 1) {
        this.hideForms();
      }
      else {
        this.activeIds = name.split(" ");
      }
    }
  }

  showGeneralAndFacility() {
    this.showSection('general-outcomes facility-data');
  }

  showGeneralOutcomes() {
    this.showSection('general-outcomes');
  }

  showFacilityData() {
    this.showSection('facility-data');
  }

  showConclusions() {
    this.showSection('outcomes-conclusions');
  }

  processWorkflowActionMaybe(action: WorkflowAction) {
    if (this.actionRequiresReason(action)) {
      var activeModal = this.modalService.open(this.modalContent, { ariaLabelledBy: 'modal-basic-title', size: 'xl' });
      activeModal.result
        .then(
          (result) => {
            this.outcome.reason = this.rejectComment;
            this.processWorkflowAction(action);
            this.rejectComment = null;
          },
          (reject) => console.log('Workflow action modal dismissed: ' + reject)
        )
        .catch(() => console.error('Error in workflow action modal'));
    } else {
      if (this.actionClearsReason(action)) this.outcome.reason = null;
      this.processWorkflowAction(action);
    }
  }

  processWorkflowAction(action: WorkflowAction) {
    this.outcomesService.submitWorkflowAction(action.value, this.outcome, this.outcomeData);
  }

  generateStatusRows(outcome: Outcome) {
    this.outcomeProvisionalSubmitted = StatusBadge.Empty;
    this.outcomeProvisionalAccepted = StatusBadge.Empty;
    this.outcomeFinalSubmitted = StatusBadge.Empty;
    this.outcomeFinalAccepted = StatusBadge.Empty;
    this.reportSubmitted = StatusBadge.Empty;
    this.reportAccepted = StatusBadge.Empty;
    switch (outcome.outcomeStatusId) {
      case 1: // Data Submission Pending
        this.statusRows = [
          { status: StatusBadge.Started, topText: "Pending", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = StatusBadge.Started;
        break;
      case 2: // Data Submission Draft
        this.statusRows = [
          { status: StatusBadge.Started, topText: "Draft", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = StatusBadge.Started;
        break;
      case 3: // Data Submission Submitted
        this.statusRows = [
          { status: StatusBadge.Started, topText: "Submitted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = StatusBadge.Complete;
        this.outcomeProvisionalAccepted = StatusBadge.Started;
        break;
      case 4: // Data Submission Accepted AND Data Review Pending
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Started, topText: "Pending", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = StatusBadge.Complete;
        this.outcomeProvisionalAccepted = StatusBadge.Complete;
        break;
      case 5: // Data Submission Not Accepted
        this.statusRows = [
          { status: StatusBadge.Failed, topText: "Not Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = StatusBadge.Complete;
        this.outcomeProvisionalAccepted = StatusBadge.Failed;
        break;
      case 6: // Data Review Draft
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Started, topText: "Draft", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = StatusBadge.Complete;
        this.outcomeFinalSubmitted = StatusBadge.Started;
        break;
      case 7: // Data Review Submitted
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Started, topText: "Submitted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = StatusBadge.Complete;
        this.outcomeFinalSubmitted = StatusBadge.Complete;
        this.outcomeFinalAccepted = StatusBadge.Started;
        break;
      case 8: // Data Review Accepted AND Final Review Pending
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Started, topText: "Pending", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = StatusBadge.Complete;
        this.outcomeFinalSubmitted = StatusBadge.Complete;
        this.outcomeFinalAccepted = StatusBadge.Complete;
        break;
      case 9: // Data Review Not Accepted
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Failed, topText: "Not Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Empty, topText: "Upcoming", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = StatusBadge.Complete;
        this.outcomeFinalSubmitted = StatusBadge.Complete;
        this.outcomeFinalAccepted = StatusBadge.Failed;
        break;
      case 10: // Final Review Draft
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Started, topText: "Draft", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = this.outcomeFinalSubmitted = this.outcomeFinalAccepted = StatusBadge.Complete;
        this.reportSubmitted = StatusBadge.Started;
        break;
      case 11: // Final Review Submitted
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Started, topText: "Submitted", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = this.outcomeFinalSubmitted = this.outcomeFinalAccepted = StatusBadge.Complete;
        this.reportSubmitted = StatusBadge.Complete;
        this.reportAccepted = StatusBadge.Started;
        break;
      case 12: // Final Review Accepted
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = StatusBadge.Complete;
        this.outcomeFinalSubmitted = this.outcomeFinalAccepted = StatusBadge.Complete;
        this.reportSubmitted = StatusBadge.Complete;
        this.reportAccepted = StatusBadge.Complete;
        break;
      case 13: // Final Review Not Accepted
        this.statusRows = [
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepOneLabel, actionUser: outcome.submissionUserName, actionTimestamp: outcome.submissionTimestamp },
          { status: StatusBadge.Complete, topText: "Accepted", bottomText: this.stepTwoLabel, actionUser: outcome.reviewUserName, actionTimestamp: outcome.reviewTimestamp },
          { status: StatusBadge.Failed, topText: "Not Accepted", bottomText: this.stepThreeLabel, actionUser: outcome.finalUserName, actionTimestamp: outcome.finalTimestamp }
        ];
        this.outcomeProvisionalSubmitted = this.outcomeProvisionalAccepted = this.outcomeFinalSubmitted = this.outcomeFinalAccepted = StatusBadge.Complete;
        this.reportSubmitted = StatusBadge.Complete;
        this.reportAccepted = StatusBadge.Failed;
        break;
    }

    this.outcomeProvisionalStatus = this.outcomeProvisionalSubmitted === this.outcomeProvisionalAccepted ? this.outcomeProvisionalSubmitted : StatusBadge.Started;
    this.outcomeFinalDataStatus = this.outcomeFinalSubmitted === this.outcomeFinalAccepted ? this.outcomeFinalSubmitted : StatusBadge.Started;
  }

  hideForms = () => this.activeIds = [];

  checkTimeseriesTypeId = (instrumentData: OutcomeFacilityInstrumentData, propertyName: keyof OutcomeFacilityInstrumentData, typeId: number) => getProperty(instrumentData, propertyName) === typeId;

  stationDataUrl = (stationNumber: string, startDate: string, endDate: string) => {
    return `https://nednr.aquaticinformatics.net/Data/DataSet/Grid/Location/${stationNumber}/DataSet/Discharge/Daily%20Mean/Interval/Custom/${startDate}/${endDate}`;
  }

  appropriatorEditEnabled = () => !!this.outcome && [1, 2, 5].includes(this.outcome.outcomeStatusId);

  dnrEditEnabled = () => !!this.outcome && [4, 6, 9].includes(this.outcome.outcomeStatusId);

  finalEditEnabled = () => !!this.outcome && [8, 10, 13].includes(this.outcome.outcomeStatusId);

  actionRequiresReason = (action: WorkflowAction) => [OutcomeWorkflowAction.RejectData, OutcomeWorkflowAction.RejectReview,
    OutcomeWorkflowAction.RejectFinal, OutcomeWorkflowAction.Restart].includes(action.value);
  actionClearsReason = (action: WorkflowAction) => [OutcomeWorkflowAction.SubmitData, OutcomeWorkflowAction.SubmitReview,
    OutcomeWorkflowAction.SubmitFinal].includes(action.value);
}

function getProperty<T, K extends keyof T>(o: T, propertyName: K): T[K] {
  return o[propertyName];
}
